import React, { useEffect, useState } from "react";
import { useGetUserActivityQuery } from "../../../store/api/userApi";
import { Link, useLocation } from "react-router-dom";
import { getDayDifference, renderTextWithLinks } from "../../../helpers";
import { BtnLoader, Dot } from "../../../icons";
import ExpandableParagraph from "../../Home/main-content/feed/expandable-paragraph";
import { PollOption } from "../../Home/main-content/feed/feed-card";
import moment from "moment";
import { ROUTES } from "../../../constants/routes";

export interface Poll {
  options?: PollOption[];
  expires_at?: string;
  allow_multiple?: boolean;
}

export interface PostDetail {
  id?: string;
  postMessage?: string;
  postImage?: string;
  likes?: number;
  comments?: number;
  liked?: boolean;
  is_bookmarked?: boolean;
  tags?: string[];
  poll?: Poll;
  already_voted?: boolean;
  selected_options?: string[];
  created_at?: string | Date;
  pinned?: boolean;
  attachments?: {
    images: string[];
    video: string;
    audio: string;
    file: string;
  };
  user?: {
    id?: string;
    profile_picture?: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    designation?: string;
  };
  is_pinned?: boolean;
  updated_at?: Date;
}

export interface CommentDetail {
  created_at: string;
  id: string;
  reaction_message: string;
  activity_id?: string;
  user: {
    designation: string;
    first_name: string;
    id: string;
    last_name: string;
    profile_picture: string;
    username: string;
  };
  parent_id: string;
}

export enum ActivityType {
  POSTS = "activity",
  COMMENTS = "comment",
}

export const ActivityTypeDisplayName: Record<ActivityType, string> = {
  [ActivityType.POSTS]: "Posts",
  [ActivityType.COMMENTS]: "Comments",
};

const getPostType = (post: PostDetail) => {
  if (post.attachments?.images?.length) {
    return "image.";
  }
  if (post.attachments?.video) {
    return "video.";
  }
  if (post.attachments?.file) {
    return "file.";
  }
  if (post.poll?.options?.length) {
    return "poll.";
  }

  return ".";
};

const PostPreview = ({ post }: { post: PostDetail }) => {
  const [totalVotes, setTotalVotes] = useState(0);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isExpired, setIsExpired] = useState(false);
  const { poll } = post;
  useEffect(() => {
    if (poll && poll.options) {
      setTotalVotes(
        poll.options.reduce(
          (sum: number, option: PollOption) => sum + option.votes,
          0,
        ),
      );
    }
  }, [poll]);

  useEffect(() => {
    const time = calculateTimeLeft(poll?.expires_at || "");
    setTimeLeft(time);
  }, [poll?.expires_at]);

  const calculateTimeLeft = (expires_at: string) => {
    const now = moment();
    const expiresAt = moment(expires_at);
    const hoursDiff = expiresAt.diff(now, "hours");

    if (hoursDiff <= 0) {
      setIsExpired(true);
    }

    const displayTime =
      hoursDiff >= 24
        ? `${expiresAt.diff(now, "days")} days`
        : `${hoursDiff} hours`;

    return displayTime;
  };

  return (
    <div className="flex flex-col border-b border-[#e1e1e1] py-4 gap-3">
      <div className="flex justify-start">
        <p className="text-[#090909] text-[13px] font-bold leading-[18px]">
          {post?.user?.first_name + " " + post?.user?.last_name}{" "}
          <span className="font-normal">posted this {getPostType(post)} </span>
          <span className="text-[#999999] text-[13px] font-normal ">
            {getDayDifference(post?.created_at)}
          </span>
        </p>
      </div>

      <div className="flex gap-3">
        {post?.attachments?.images?.length ? (
          <img
            src={post.attachments?.images[0]}
            alt="post"
            className="w-20 h-20 rounded-md object-contain"
          />
        ) : null}
        <p className="font-normal text-md text-primary-900">
          <ExpandableParagraph
            text={renderTextWithLinks(post.postMessage || "")}
            postId={post?.id || ""}
          />
        </p>
        {post?.poll?.options && post?.poll?.options?.length > 0 && (
          <div className="text-[14px] text-[#808080] flex flex-row items-center gap-x-1">
            <div>{totalVotes ? totalVotes + " votes" : "0 votes "}</div>
            <Dot fillColor="#808080" />
            {<div> {isExpired ? "Expired" : timeLeft}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export const CommentPreview = ({
  comment,
  showBorder = true,
}: {
  comment: CommentDetail;
  showBorder?: boolean;
}) => {
  return (
    <div
      className={`flex flex-col ${showBorder ? "border-b border-[#e1e1e1]" : " "} py-4 gap-3 `}
    >
      <div className="flex justify-start ">
        <p className="text-[#090909] text-[13px] font-bold leading-[18px]">
          {comment?.user?.first_name + " " + comment?.user?.last_name}{" "}
          <span className="font-normal">{"commented on a post. "}</span>
          <span className="text-[#999999] text-[13px] font-normal ">
            {getDayDifference(comment?.created_at)}
          </span>
        </p>
      </div>
      <Link
        to={ROUTES.ACTIVITY.replace(
          ":activity_id",
          comment?.activity_id
            ? comment.activity_id || ""
            : comment.parent_id || "",
        )}
      >
        <div className="text-[#090909] text-md font-normal font-['SF Pro'] leading-tight">
          {comment.reaction_message}
        </div>
      </Link>
    </div>
  );
};

const Activity = () => {
  const [activityType, setActivityType] = useState<ActivityType>(
    ActivityType.POSTS,
  );
  const location = useLocation();
  const userId = location.pathname.split("/").pop();
  const { data: activityData, isFetching } = useGetUserActivityQuery(
    {
      user_id: userId,
      limit: 4,
      offset: 0,
      type: activityType,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    switch (activityType) {
      case ActivityType.POSTS:
        setShowAll(
          activityData?.data?.posts?.length &&
            activityData?.data?.posts?.length > 0,
        );
        break;
      case ActivityType.COMMENTS:
        setShowAll(
          activityData?.data?.reactions?.length &&
            activityData?.data?.reactions?.length > 0,
        );
    }
  }, [activityData, activityType]);

  return (
    <div className="bg-white rounded-lg p-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Activity</h1>
      </div>
      <div className="flex justify-between mt-4">
        <div className="flex space-x-4">
          {Object.entries(ActivityTypeDisplayName).map(([key, value]) => (
            <button
              key={key}
              onClick={() => setActivityType(key as ActivityType)}
              className={`${
                activityType === key
                  ? "bg-[#007AFF] text-white"
                  : "border-[#D7D7D7] border-[1px] bg-white text-black"
              } px-4 py-1 rounded-2xl text-sm capitalize`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col">
        {activityType === ActivityType.POSTS
          ? activityData?.data?.posts?.map((post: PostDetail) => (
              <PostPreview post={post as PostDetail} />
            ))
          : activityData?.data?.reactions?.map((comment: CommentDetail) => (
              <CommentPreview comment={comment} />
            ))}
        {showAll ? (
          <div className="flex w-full justify-center pt-4">
            <Link to={"activity"} state={activityType}>
              <div className="text-md font-normal text-primary-900 hover:cursor-pointer">
                Show all
              </div>
            </Link>
          </div>
        ) : (
          <div className="flex w-full justify-center text-md font-normal text-primary-900">
            No activity
          </div>
        )}
        {isFetching && <BtnLoader />}
      </div>
    </div>
  );
};

export default Activity;
